import Application from "../../Application";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IOrganisationIndex } from "../../organisations/model/OrganisationIndex";
import CreateAttachmentFileInteractor from "../interactor/attachments/CreateAttachmentFileInteractor";
import CreateAttachmentInteractor from "../interactor/attachments/CreateAttachmentInteractor";
import GetAllCaseLawDocumentAttachmentsInteractor from "../interactor/attachments/GetAllCaseLawDocumentAttachmentsInteractor";
import RemoveCaseLawDocumentAttachmentInteractor from "../interactor/attachments/RemoveCaseLawDocumentAttachment";
import GetCaseLawDocumentInteractor from "../interactor/GetCaseLawDocumentInteractor";
import PublishCaseLawDocumentInteractor from "../interactor/PublishCaseLawDocumentInteractor";
import RevertCaseLawDocumentStatusInteractor from "../interactor/RevertCaseLawDocumentStatusInteractor";
import { IAttachment } from "../model/Attachment";
import { IAttachmentType } from "../../attachmentType/model/AttachmentType";
import { ICaseLawDocument } from "../model/CaseLawDocument";

import { IOrganisation } from "../../organisations/model/Organisation";
import AttachmentPresentationModel, {
  TAttachmentPresentationModel
} from "./model/AttachmentPresentationModel";
import AttachmentTypePresentationModel, {
  TAttachmentTypePresentationModel
} from "../../organisations/presenter/model/AttachmentTypePresentationModel";
import CaseLawDocumentPresentationModel, {
  TCaseLawDocumentPresentationModel
} from "./model/CaseLawDocumentPresentationModel";
import { IUser } from "../../user/model/User";
import ArchiveCaseLawDocumentInteractor from "../interactor/ArchiveCaseLawDocumentInteractor";
import OrganisationIndexPresentationModel, {
  TOrganisationIndexPresentationModel
} from "../../organisations/presenter/model/OrganisationIndexPresentationModel";
import {
  IDocumentType,
  TDocumentType
} from "../../documentTypes/model/DocumentType";
import DeleteCaseLawDocumentInteractor from "../interactor/DeleteCaseLawDocumentInteractor";

export interface TCaseLawDocumentDetailsPresenter
  extends TLoadingAwarePresenter {
  caseLawDocument: TCaseLawDocumentPresentationModel;
  organisations: IOrganisation[];
  caseLawDocumentTypes: TDocumentType[];
  caseLawDocumentIndexes: TOrganisationIndexPresentationModel[];
  attachmentTypes: TAttachmentTypePresentationModel[];
  attachmentContentTypes: IGenericCodebookItem[];
  caseLawDocumentAttachments: TAttachmentPresentationModel[];
  uploadedFile: any;
  indexHierarchyList: { id: number; path: string[] }[];
  user?: IUser;
  invalidatedCaseLawDocuments: TCaseLawDocumentPresentationModel[];
  decisionTypes: IGenericCodebookItem[];
}

export interface ICaseLawDocumentDetailsPresenter
  extends TCaseLawDocumentDetailsPresenter,
    TPresentable {
  loadCaseLawDocument(caseLawDocuments: ICaseLawDocument): void;
  loadReferenceValues(
    caseLawDocumentTypes: IDocumentType[],
    organisations: IOrganisation[],
    attachmentTypes: IAttachmentType[],
    attachmentContentTypes: IGenericCodebookItem[],
    invalidatedCaseLawDocuments: ICaseLawDocument[],
    decisionTypes: IGenericCodebookItem[]
  ): void;
  loadAttachments(caseLawDocumentAttachments: IAttachment[]): void;
  loadCaseLawDocumentIndexes(caseLawDocumentIndexes: IOrganisationIndex[]);
  createAttachment(attachment: IAttachment): void;
  setFileValue(file): void;
  updateAttachment(attachment: any): void;
  removeAttachment(id: number): void;
  publishCaseLawDocument(): void;
  revertCaseLawDocumentStatus(): void;
  showCaseLawDocumentUpdatePage(id: number): void;
  showCaseLawDocumentDetailsPage(id: number): void;
  archiveCaseLawDocument(): void;
  canEdit(organisationId: number): boolean;
  deleteCaseLawDocument(): void;
}

const defaultState: TCaseLawDocumentDetailsPresenter = {
  caseLawDocument: {} as TCaseLawDocumentPresentationModel,
  organisations: [],
  caseLawDocumentTypes: [],
  caseLawDocumentIndexes: [],
  attachmentTypes: [],
  attachmentContentTypes: [],
  caseLawDocumentAttachments: [],
  uploadedFile: undefined,
  indexHierarchyList: [],
  decisionTypes: [],
  invalidatedCaseLawDocuments: []
};

const CaseLawDocumentDetailsPresenter = withStore<
  ICaseLawDocumentDetailsPresenter,
  TCaseLawDocumentDetailsPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): ICaseLawDocumentDetailsPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TCaseLawDocumentDetailsPresenter>();
    loader.start("caseLawDocumentLoader");
    const loadCaseLawDocument = (item: ICaseLawDocument) => {
      const { organisations, caseLawDocumentTypes } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();

      const caseLawDocumentType = caseLawDocumentTypes?.find(
        (value) => value.id === item.documentTypeId
      );
      const organisation = organisations?.find(
        (value) => value.id === item.organisationId
      );
      const selectedLanguage = _store.getState<any>().selectedLanguage;
      const summary = item.summaryTranslations.find(
        (item) => item.languageId === selectedLanguage.id
      )?.summaryTranslation;

      const caseLawDocument = CaseLawDocumentPresentationModel(
        item,
        caseLawDocumentType,
        organisation,
        summary
      );

      _store.update({
        caseLawDocument
      });
      loader.stop("caseLawDocumentLoader");
    };
    const loadReferenceValues = (
      caseLawDocumentTypes: IDocumentType[],
      organisations: IOrganisation[],
      attachmentTypesList: IAttachmentType[],
      attachmentContentTypes: IGenericCodebookItem[],
      invalidatedCaseLawDocumentList: ICaseLawDocument[],
      decisionTypes: IGenericCodebookItem[]
    ) => {
      let attachmentTypes = (attachmentTypesList || []).map((item) => {
        const contentType = attachmentContentTypes?.find(
          (value) => value.id === item.attachmentContentTypeId
        );
        return AttachmentTypePresentationModel(item, contentType);
      });
      const invalidatedCaseLawDocuments = invalidatedCaseLawDocumentList.map(
        (item) => {
          const caseLawDocumentType = caseLawDocumentTypes?.find(
            (value) => value.id === item.documentTypeId
          );
          const organisation = organisations?.find(
            (value) => value.id === item.organisationId
          );
          const selectedLanguage = _store.getState<any>().selectedLanguage;
          const summary = item.summaryTranslations.find(
            (item) => item.languageId === selectedLanguage.id
          )?.summaryTranslation;

          return CaseLawDocumentPresentationModel(
            item,
            caseLawDocumentType,
            organisation,
            summary
          );
        }
      );
      const user = _store.getState<any>().user;

      _store.update({
        caseLawDocumentTypes,
        organisations,
        attachmentContentTypes,
        attachmentTypes,
        user,
        invalidatedCaseLawDocuments,
        decisionTypes
      });
    };

    const canEdit = (organisationId) => {
      const { user } = _store.getState<TCaseLawDocumentDetailsPresenter>();
      if (user) {
        return user.canEdit(organisationId);
      } else {
        return false;
      }
    };

    const loadCaseLawDocumentIndexes = (
      caseLawDocumentIndexes: IOrganisationIndex[]
    ) => {
      const { selectedLanguage } = _store.getState<any>();
      _store.update({
        caseLawDocumentIndexes: caseLawDocumentIndexes.map((item) =>
          OrganisationIndexPresentationModel(item, selectedLanguage)
        )
      });
      prepareHierarchyList();
    };

    const getCorrectIndexTranslation = (index: any) => {
      const { selectedLanguage } = store.getState<any>();
      const nameTranslation = index.nameTranslations.find(
        (item) => item.languageId === selectedLanguage?.id
      )?.nameTranslation;
      return nameTranslation ?? index.name;
    };

    const getCorrectGroupTranslation = (data?: any) => {
      const { selectedLanguage } = store.getState<any>();
      if (data) {
        const nameTranslation = data.nameTranslations.find(
          (item) => item.languageId === selectedLanguage?.id
        )?.nameTranslation;
        return nameTranslation ?? data.name;
      } else {
        return "";
      }
    };
    const findParent = (group, groupList, indexHierarchyList) => {
      if (group?.parentId) {
        let g = groupList.find((g) => g.id === group?.parentId);
        if (g) {
          indexHierarchyList.push(getCorrectGroupTranslation(g));
          return findParent(g, groupList, indexHierarchyList);
        }
      } else if (group?.organisationIndexCategoryId) {
        indexHierarchyList.push(group?.categoryName);
      }
      return indexHierarchyList;
    };

    const prepareHierarchyList = () => {
      const { caseLawDocumentIndexes } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();

      let result = caseLawDocumentIndexes.map((index) => {
        let _indexHierarchyList: string[] = [];
        let firstGroup = index.groups.find(
          (g) => g.id === index.organisationGroupId
        );
        _indexHierarchyList.push(getCorrectIndexTranslation(index));
        _indexHierarchyList.push(getCorrectGroupTranslation(firstGroup));
        _indexHierarchyList = [
          ...findParent(firstGroup, index.groups, _indexHierarchyList)
        ];
        return {
          id: index.id,
          path: _indexHierarchyList
        };
      });
      _store.update({ indexHierarchyList: result });
    };
    loader.start("attachmentLoader");
    const loadAttachments = (attachments: IAttachment[]) => {
      const { attachmentTypes } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();

      let caseLawDocumentAttachments = (attachments || []).map((item) => {
        const attachmentType = attachmentTypes?.find(
          (value) => value.id === item.attachmentTypeId
        );
        return AttachmentPresentationModel(item, attachmentType);
      });
      _store.update({
        caseLawDocumentAttachments
      });
      loader.stop("attachmentLoader");
    };

    const getCaseLawDocument = async (caseLawDocumentId: number) => {
      loader.start("caseLawDocumentLoader");
      let caseLawDocument = await _application.container
        .resolve<GetCaseLawDocumentInteractor>("getCaseLawDocument")
        .execute(caseLawDocumentId);
      loadCaseLawDocument(caseLawDocument);
      loader.stop("caseLawDocumentLoader");
    };
    const getAllAttachments = async (caseLawDocumentId: number) => {
      loader.start("attachmentLoader");
      let caseLawDocumentAttachments = await _application.container
        .resolve<GetAllCaseLawDocumentAttachmentsInteractor>(
          "getAllCaseLawDocumentAttachments"
        )
        .execute(caseLawDocumentId);
      loadAttachments(caseLawDocumentAttachments);
    };
    const publishCaseLawDocument = async () => {
      const { caseLawDocument } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();

      await _application.container
        .resolve<PublishCaseLawDocumentInteractor>("publishCaseLawDocument")
        .execute(caseLawDocument.id);
      await getCaseLawDocument(caseLawDocument.id);
    };
    const archiveCaseLawDocument = async () => {
      const { caseLawDocument } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();
      caseLawDocument.caseLawDocDate = new Date(caseLawDocument.caseLawDocDate);
      await _application.container
        .resolve<ArchiveCaseLawDocumentInteractor>("archiveCaseLawDocument")
        .execute(caseLawDocument.id);
      await getCaseLawDocument(caseLawDocument.id);
    };
    const deleteCaseLawDocument = async () => {
      const { caseLawDocument } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();
      caseLawDocument.caseLawDocDate = new Date(caseLawDocument.caseLawDocDate);
      await _application.container
        .resolve<DeleteCaseLawDocumentInteractor>("deleteCaseLawDocument")
        .execute(caseLawDocument.id);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      showCaseLawDocumentList();
    };
    const revertCaseLawDocumentStatus = async () => {
      const { caseLawDocument } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();

      await _application.container
        .resolve<RevertCaseLawDocumentStatusInteractor>(
          "revertCaseLawDocumentStatus"
        )
        .execute(caseLawDocument.id);
      await getCaseLawDocument(caseLawDocument.id);
    };
    const createAttachment = async (attachment: any) => {
      const { caseLawDocument, uploadedFile } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();
      loader.start("attachmentLoader");
      uploadedFile && !Object.keys(uploadedFile).includes("isDummy")
        ? await _application.container
            .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
            .execute(attachment, uploadedFile, caseLawDocument.id)
        : await _application.container
            .resolve<CreateAttachmentInteractor>("createAttachment")
            .execute(attachment, caseLawDocument.id);
      setFileValue(undefined);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      loader.stop("attachmentLoader");
      await getAllAttachments(caseLawDocument.id);
      await getCaseLawDocument(caseLawDocument.id);
      return true;
    };
    const updateAttachment = async (attachment: any) => {
      const { caseLawDocument, uploadedFile } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();
      loader.start("attachmentLoader");
      uploadedFile && !Object.keys(uploadedFile).includes("isDummy")
        ? await _application.container
            .resolve<CreateAttachmentFileInteractor>("createAttachmentFile")
            .execute(attachment, uploadedFile, caseLawDocument.id)
        : await _application.container
            .resolve<CreateAttachmentInteractor>("createAttachment")
            .execute(attachment, caseLawDocument.id);
      setFileValue(undefined);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      loader.stop("attachmentLoader");
      await getAllAttachments(caseLawDocument.id);
      await getCaseLawDocument(caseLawDocument.id);
      return true;
    };
    const setFileValue = (file: any) => {
      _store.update({ uploadedFile: file });
    };
    const removeAttachment = async (id: number) => {
      const { caseLawDocument } =
        _store.getState<TCaseLawDocumentDetailsPresenter>();
      await application.container
        .resolve<RemoveCaseLawDocumentAttachmentInteractor>(
          "removeCaseLawDocumentAttachment"
        )
        .execute(caseLawDocument.id, id);
      await getAllAttachments(caseLawDocument.id);
      await getCaseLawDocument(caseLawDocument.id);
    };
    const showCaseLawDocumentUpdatePage = (id: number) => {
      return _application.navigator.push({
        pathname: `/case-law-documents/${id.toString()}/update`
      });
    };
    const showCaseLawDocumentList = () => {
      return _application.navigator.push({
        pathname: `/search`
      });
    };
    const showCaseLawDocumentDetailsPage = (id: number) => {
      return _application.navigator.push({
        pathname: `/case-law-documents/${id.toString()}/details`
      });
    };
    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadCaseLawDocument,
      loadReferenceValues,
      loadCaseLawDocumentIndexes,
      loadAttachments,
      createAttachment,
      setFileValue,
      updateAttachment,
      removeAttachment,
      publishCaseLawDocument,
      archiveCaseLawDocument,
      revertCaseLawDocumentStatus,
      showCaseLawDocumentUpdatePage,
      showCaseLawDocumentDetailsPage,
      canEdit,
      deleteCaseLawDocument
    };
  },
  defaultState
);

export default CaseLawDocumentDetailsPresenter;
